/**
 * Hooks de drafts de templates gerados via IA — TH-55175 (M-IAtpl.6).
 *
 * Wraps em TanStack Query/Mutation para os 4 endpoints da galeria:
 *
 * - `GET /api/templates/drafts` (lista filtrável; já existia em TH-55171).
 * - `GET /api/templates/drafts/:id` (detalhe individual; criado nesta issue).
 * - `PATCH /api/templates/drafts/:id` (aprovar/rejeitar; criado nesta issue).
 * - `POST /api/templates/drafts/:id/regenerate` (nova tentativa; criado nesta issue).
 *
 * O list query faz polling a cada 5s **enquanto houver drafts em
 * `pending` ou `generating`** — quando todos os drafts da página
 * atual estão em estado terminal (`draft`/`pending_review`/`approved`/
 * `rejected`/`failed`), o polling para automaticamente para não
 * desperdiçar request. Em telas vazias também não polleia.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

// Espelha os 7 valores válidos no DB (CHECK constraint
// `ck_template_drafts_status` — packages/db/.../20260520_1100...).
export type TemplateDraftStatus =
  | "pending"
  | "generating"
  | "draft"
  | "pending_review"
  | "approved"
  | "rejected"
  | "failed";

// Slugs canônicos do PromptCategory (TH-55172).
export type TemplateDraftCategory =
  | "hortifruti"
  | "acougue"
  | "padaria"
  | "mercearia"
  | "bebidas"
  | "promo"
  | "limpeza"
  | "sazonal";

export type TemplateDraftStyle =
  | "minimalista"
  | "complexo"
  | "combo_a_b"
  | "sazonal";

export interface TemplateDraftRead {
  id: string;
  session_id: string;
  category: TemplateDraftCategory | null;
  style: TemplateDraftStyle | null;
  prompt_version: number | null;
  prompt_params: Record<string, string> | null;
  brief: Record<string, any>;
  ai_prompt: string;
  ai_model_id: string;
  // Decimals chegam como string do FastAPI.
  ai_cost_brl: string;
  preview_url: string;
  status: TemplateDraftStatus;
  approved_template_id: string | null;
  created_by_user_id: string | null;
  created_by_tenant_id: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  prompt_text_preview: string;
}

export interface DraftsListFilters {
  status?: TemplateDraftStatus;
  category?: TemplateDraftCategory;
  style?: TemplateDraftStyle;
  page?: number;
  per_page?: number;
}

function buildQuery(filters: DraftsListFilters): string {
  const sp = new URLSearchParams();
  if (filters.status) sp.set("status", filters.status);
  if (filters.category) sp.set("category", filters.category);
  if (filters.style) sp.set("style", filters.style);
  if (filters.page) sp.set("page", String(filters.page));
  if (filters.per_page) sp.set("per_page", String(filters.per_page));
  return sp.toString();
}

async function fetchDrafts(
  filters: DraftsListFilters,
): Promise<TemplateDraftRead[]> {
  const qs = buildQuery(filters);
  const url = qs ? `/api/templates/drafts?${qs}` : `/api/templates/drafts`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as TemplateDraftRead[];
}

const IN_FLIGHT_STATUSES: ReadonlySet<TemplateDraftStatus> = new Set([
  "pending",
  "generating",
]);

export function useTemplateDrafts(
  filters: DraftsListFilters = {},
): UseQueryResult<TemplateDraftRead[], Error> {
  return useQuery({
    queryKey: ["template-drafts", filters],
    queryFn: () => fetchDrafts(filters),
    // Polling enquanto houver drafts in-flight; senão, sem polling.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.length === 0) return false;
      const hasInFlight = data.some((d) => IN_FLIGHT_STATUSES.has(d.status));
      return hasInFlight ? 5000 : false;
    },
    staleTime: 0,
  });
}

async function fetchDraftById(id: string): Promise<TemplateDraftRead> {
  const res = await fetch(`/api/templates/drafts/${encodeURIComponent(id)}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as TemplateDraftRead;
}

export function useTemplateDraft(
  id: string | null,
): UseQueryResult<TemplateDraftRead, Error> {
  return useQuery({
    queryKey: ["template-draft", id],
    queryFn: () => fetchDraftById(id as string),
    enabled: !!id,
  });
}

export interface ReviewDraftInput {
  id: string;
  status: "approved" | "rejected";
  rejection_reason?: string;
}

async function reviewDraft(input: ReviewDraftInput): Promise<TemplateDraftRead> {
  const res = await fetch(
    `/api/templates/drafts/${encodeURIComponent(input.id)}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        status: input.status,
        rejection_reason: input.rejection_reason ?? null,
      }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    const detail =
      typeof body.detail === "string"
        ? body.detail
        : `Falha ao atualizar draft (HTTP ${res.status}).`;
    throw new Error(detail);
  }
  return (await res.json()) as TemplateDraftRead;
}

export function useReviewTemplateDraft(): UseMutationResult<
  TemplateDraftRead,
  Error,
  ReviewDraftInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reviewDraft,
    onSuccess: (updated) => {
      // Refresh galeria + detalhe.
      void qc.invalidateQueries({ queryKey: ["template-drafts"] });
      qc.setQueryData(["template-draft", updated.id], updated);
    },
  });
}

export interface RegenerateDraftResult {
  draft_ids: string[];
  estimated_cost_brl: string;
}

async function regenerateDraft(id: string): Promise<RegenerateDraftResult> {
  const res = await fetch(
    `/api/templates/drafts/${encodeURIComponent(id)}/regenerate`,
    {
      method: "POST",
      credentials: "include",
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    const detail =
      typeof body.detail === "string"
        ? body.detail
        : `Falha ao regerar draft (HTTP ${res.status}).`;
    throw new Error(detail);
  }
  return (await res.json()) as RegenerateDraftResult;
}

export function useRegenerateTemplateDraft(): UseMutationResult<
  RegenerateDraftResult,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: regenerateDraft,
    onSuccess: () => {
      // Novo draft entra na fila — invalida lista para ele aparecer.
      void qc.invalidateQueries({ queryKey: ["template-drafts"] });
    },
  });
}
